import { supabase } from "@/lib/supabaseClient";

const AFFILIATE_ATTRIBUTION_KEY = "xarith_affiliate_attribution";
const AFFILIATE_VISITOR_ID_KEY = "xarith_affiliate_visitor_id";
const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;

type StoredAffiliateAttribution = {
  affiliateId: string;
  affiliateCode: string;
  visitId: string;
  visitorId: string;
  capturedAt: string;
  expiresAt: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
};

function normalizeCode(value: string | null) {
  const code = (value ?? "").trim().toLowerCase();
  return /^[a-z0-9][a-z0-9_-]{1,63}$/.test(code) ? code : "";
}

function getVisitorId() {
  const existing = localStorage.getItem(AFFILIATE_VISITOR_ID_KEY);
  if (existing && existing.length >= 12) return existing;

  const generated = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(AFFILIATE_VISITOR_ID_KEY, generated);
  return generated;
}

function loadStoredAttribution(): StoredAffiliateAttribution | null {
  try {
    const raw = localStorage.getItem(AFFILIATE_ATTRIBUTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAffiliateAttribution;
    if (!parsed.affiliateId || !parsed.visitId || !parsed.expiresAt) return null;
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(AFFILIATE_ATTRIBUTION_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function getStoredAffiliateAttribution() {
  if (typeof window === "undefined") return null;
  return loadStoredAttribution();
}

export async function captureAffiliateAttributionFromUrl() {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  const ref = normalizeCode(params.get("ref"));
  if (!ref) return;

  const existing = loadStoredAttribution();
  if (existing) return;

  const visitorId = getVisitorId();
  const landingPath = `${window.location.pathname}${window.location.search}`;

  const { data, error } = await supabase.functions.invoke("track-affiliate-visit", {
    body: {
      ref,
      visitor_id: visitorId,
      landing_path: landingPath,
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      utm_content: params.get("utm_content"),
      utm_term: params.get("utm_term"),
      referrer: document.referrer || null,
    },
  });

  if (error || !data?.affiliate_id || !data?.visit_id) {
    return;
  }

  const capturedAt = new Date();
  const expiresAt = new Date(capturedAt.getTime() + ATTRIBUTION_TTL_MS);
  const attribution: StoredAffiliateAttribution = {
    affiliateId: String(data.affiliate_id),
    affiliateCode: String(data.affiliate_code ?? ref),
    visitId: String(data.visit_id),
    visitorId,
    capturedAt: capturedAt.toISOString(),
    expiresAt: expiresAt.toISOString(),
    utmSource: params.get("utm_source") ?? undefined,
    utmMedium: params.get("utm_medium") ?? undefined,
    utmCampaign: params.get("utm_campaign") ?? undefined,
    utmContent: params.get("utm_content") ?? undefined,
    utmTerm: params.get("utm_term") ?? undefined,
  };

  localStorage.setItem(AFFILIATE_ATTRIBUTION_KEY, JSON.stringify(attribution));
}

export async function claimStoredAffiliateAttribution(userId: string) {
  if (typeof window === "undefined") return;
  const attribution = loadStoredAttribution();
  if (!attribution) return;

  const { error } = await supabase.from("affiliate_attributions").insert({
    user_id: userId,
    affiliate_id: attribution.affiliateId,
    first_visit_id: attribution.visitId,
    expires_at: attribution.expiresAt,
  });

  if (error && error.code !== "23505") {
    throw error;
  }
}
